/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, RefreshControl, ScrollView, Text, View } from 'react-native';
import { styles } from './ReportsScreen.styles';
import { spacing } from '../../shared/theme/spacing';
import { signalsForOperator } from '../domain/evaluatePersonalDashboard';
import type { PersonalOperatorFacts } from '../domain/reportsPersonalTypes';
import type { ReportsScreenViewModel } from './useReportsScreen';
import { formatReportsPersonalDataSources, getReportsDateRange, isSalesTimeBandsApplicable } from './reportsScreen.utils';
import { ReportsBlockHeading } from './components/ReportsBlockHeading';
import { ReportsPersonalOperatorDetailModal } from './components/ReportsPersonalOperatorDetailModal';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { planAllowsCustomer } from '../../shell/feature-flags/planEntitlementsService';
import { ReportsScopeFiltersPanel } from './components/ReportsScopeFiltersPanel';
import { ReportsPersonalSummaryIndicators } from './components/ReportsPersonalSummaryIndicators';
import { ReportsPersonalSignalsSection } from './components/ReportsPersonalSignalsSection';
import { ReportsPersonalOperatorRow } from './components/ReportsPersonalOperatorRow';
import { ReportsDataSourceFooter } from './components/ReportsDataSourceFooter';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketListRow } from '../../shared/ticket/TicketListRow'
import { TicketDeskFooter } from '../../shared/ticket/TicketDeskFooter'
import { ticketColors } from '../../shared/theme/ticketColors';
import { useReportsVentasLayout } from './reportsVentasLayout';
import { REPORTS_INFORMES_LIST_PAGE_SIZE } from './reportsScreen.constants';
import { formatMoney } from '../../shared/format/public-api';

function operatorKey(row: PersonalOperatorFacts): string {
  return row.cashierUserId ?? '__no_cashier__';
}

export function ReportsPersonalView({ vm }: { vm: ReportsScreenViewModel }) {
  const ventasLayout = useReportsVentasLayout();
  const { entitlements } = usePlanEntitlements();
  const showCustomerSection = planAllowsCustomer(entitlements);
  const [detailRow, setDetailRow] = useState<PersonalOperatorFacts | null>(null);
  const [rankingPage, setRankingPage] = useState(0);

  const operators = vm.personalOperatorsForRanking;
  const dashboard = vm.personalDashboard;
  const soloMode = dashboard?.teamMode === 'solo';
  const pageSize = vm.personalRankingPageSize ?? REPORTS_INFORMES_LIST_PAGE_SIZE;
  const periodBusy = vm.loading || vm.personalLoading;

  const operatorIdsKey = useMemo(() => operators.map((r) => operatorKey(r)).join('|'), [operators]);

  useEffect(() => {
    setRankingPage(0);
  }, [vm.dateRangeLabel, operatorIdsKey, vm.reportsQueryScope]);

  const sortedOperators = useMemo(
    () => [...operators].sort((a, b) => b.salesTotal - a.salesTotal),
    [operators]
  );

  const globalRankByOperatorKey = useMemo(() => {
    const map = new Map<string, number>();
    sortedOperators.forEach((row, index) => {
      map.set(operatorKey(row), index + 1);
    });
    return map;
  }, [sortedOperators]);

  const globalRankForRow = (row: PersonalOperatorFacts) =>
    globalRankByOperatorKey.get(operatorKey(row)) ?? 0;

  const periodRange = useMemo(
    () =>
      getReportsDateRange(
        new Date(),
        vm.dateRangePreset,
        vm.dateRangePreset === 'custom' ? { from: vm.customRangeFrom, to: vm.customRangeTo } : null
      ),
    [vm.dateRangePreset, vm.customRangeFrom, vm.customRangeTo]
  );

  const isSingleCalendarDay = useMemo(() => isSalesTimeBandsApplicable(periodRange), [periodRange]);

  const calendarSpanHours = useMemo(() => {
    const fromMs = new Date(periodRange.fromIso).getTime();
    const toMs = new Date(periodRange.toIso).getTime();
    const spanDays = Math.max(1, Math.floor((toMs - fromMs) / 86400000) + 1);
    return spanDays * 24;
  }, [periodRange]);

  const showFiscalRow = operators.some((r) => r.fiscalSalesCount > 0 || r.nonFiscalSalesCount > 0);
  const personalSource = dashboard?.source ?? vm.salesByCashierSource;
  const personalSourceHint = formatReportsPersonalDataSources(personalSource);

  const pagedRows = useMemo(() => {
    const start = rankingPage * pageSize;
    return sortedOperators.slice(start, start + pageSize);
  }, [sortedOperators, rankingPage, pageSize]);

  const totalPages = Math.max(1, Math.ceil(Math.max(1, sortedOperators.length) / pageSize));
  const canPrev = rankingPage > 0;
  const canNext = (rankingPage + 1) * pageSize < sortedOperators.length;

  const operatorFilterActive = vm.reportsOperatorFilterId != null;
  const rankingTitle = operatorFilterActive
    ? vm.reportsOperatorFilterLabel ?? 'Operador'
    : soloMode
      ? 'Tu actividad'
      : 'Ranking por operador';

  return (
    <ScrollView
      style={styles.reportsContentScroll}
      contentContainerStyle={[styles.content, { flexGrow: 1, paddingBottom: spacing.xxl }]}
      accessibilityState={{ busy: periodBusy }}
      refreshControl={
        <RefreshControl
          refreshing={vm.refreshing}
          onRefresh={vm.onRefresh}
          colors={[ticketColors.ink2]}
          tintColor={ticketColors.ink2}
        />
      }
    >
      {personalSourceHint ? (
        <Text style={[styles.caption, { marginBottom: spacing.sm, color: ticketColors.gold }]}>{personalSourceHint}</Text>
      ) : null}
      {vm.personalLoadError ? (
        <Text style={[styles.caption, { marginBottom: spacing.sm, color: ticketColors.bad }]}>{vm.personalLoadError}</Text>
      ) : null}

      <ReportsScopeFiltersPanel vm={vm} disabled={periodBusy} periodAccessibilityPrefix="Período personal" />

      <View style={periodBusy ? styles.ventasStaleData : undefined}>
        <ReportsPersonalSummaryIndicators
          dashboard={dashboard}
          layoutWide={ventasLayout.layoutWide}
          previousSalesTotal={vm.previousPeriodSalesSnapshot?.salesTotal}
          previousSalesCount={vm.previousPeriodSalesSnapshot?.salesCount}
        />
        <ReportsPersonalSignalsSection dashboard={dashboard} layoutWide={ventasLayout.layoutWide} />

        <View style={styles.personalRankingBlock}>
          <ReportsBlockHeading noTopMargin label={rankingTitle} />

          {operators.length > 0 ? (
            <Text style={styles.personalRankingHint}>
              {operatorFilterActive
                ? `Filtrado por ${vm.reportsOperatorFilterLabel ?? 'operador'} en ${vm.dateRangeLabel}.`
                : soloMode
                  ? 'Tu desempeño en el período.'
                  : 'Compará por facturación.'}
            </Text>
          ) : null}

          {vm.personalLoading && operators.length === 0 ? (
            <ActivityIndicator size="small" color={ticketColors.ink2} style={{ marginVertical: spacing.md }} />
          ) : operators.length === 0 ? (
            <Text style={[styles.caption, { marginTop: spacing.sm }]}>No hay ventas en este período con esos filtros.</Text>
          ) : (
            <PaperSheet padded testID="informes.list.sheet">
              {pagedRows.map((row, idx) => {
                const rank = globalRankForRow(row);
                const signalCount =
                  dashboard != null ? signalsForOperator(dashboard, row.cashierUserId).length : 0;
                const group = idx === pagedRows.length - 1 ? 'groupedLast' : 'grouped';
                return (
                  <TicketListRow
                    key={operatorKey(row)}
                    title={row.displayName}
                    subtitle={`${row.salesCount} ventas · Ticket ${formatMoney(Math.round(row.ticketAverage))}`}
                    meta={`Margen ${row.salesTotal > 0 ? `${(row.marginPct * 100).toFixed(0)}%` : '—'} · Parte ${(row.sharePct * 100).toFixed(0)}%`}
                    value={`${formatMoney(row.salesTotal)}`}
                    statusLabel={soloMode ? null : `#${rank}`}
                    statusTone={rank === 1 ? 'ok' : 'muted'}
                    chipLabel={signalCount > 0 ? `${signalCount} señales` : null}
                    chipTone={signalCount > 0 ? 'warn' : 'default'}
                    onPress={() => setDetailRow(row)}
                    showChevron
                    group={group}
                    testID={`informes.personal.row.${operatorKey(row)}`}
                  />
                );
              })}
              {sortedOperators.length > pageSize ? (
                <TicketDeskFooter
                  pageIndex={rankingPage}
                  pageSize={pageSize}
                  productCount={pagedRows.length}
                  totalCount={sortedOperators.length}
                  hasMore={canNext}
                  onPreviousPage={() => canPrev && setRankingPage((p) => Math.max(0, p - 1))}
                  onNextPage={() => canNext && setRankingPage((p) => p + 1)}
                  embedded
                />
              ) : null}
            </PaperSheet>
          )}
        </View>

        <ReportsDataSourceFooter
          lastUpdatedAt={vm.lastDashboardLoadAt}
          dataSourceHint={personalSourceHint || undefined}
        />
      </View>

      <ReportsPersonalOperatorDetailModal
        visible={detailRow != null}
        row={detailRow}
        globalRank={detailRow != null ? globalRankForRow(detailRow) : 0}
        branchTicketAverage={dashboard?.facts.branch.ticketAverage ?? 0}
        showFiscal={showFiscalRow}
        isSingleCalendarDay={isSingleCalendarDay}
        calendarSpanHours={calendarSpanHours}
        branchCashSessions={vm.branchCashSessions}
        operatorSignals={detailRow && dashboard ? signalsForOperator(dashboard, detailRow.cashierUserId) : []}
        paymentMethodNames={vm.paymentMethodNames}
        showCustomerSection={showCustomerSection}
        onClose={() => setDetailRow(null)}
      />
    </ScrollView>
  );
}
